import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import type { RootStore } from "./root.store";
import type { TProjectState, TProjectStateUsage } from "@/types/project-state";
import { PROJECT_STATE_GROUPS } from "@/constants/project-states";
import type { TProjectStateGroup } from "@/constants/project-states";
import type {
  TProjectStateCreateData,
  TProjectStateUpdateData,
} from "@/services/project-state.service";
import { isRecoverableFetchError } from "@/services/api.service";
import { waitBeforeRetry } from "@/services/retry-backoff";

// Attempts (1 initial + retries) for the on-mount central-states load. The
// Workflow tabs (project + template) fetch these once on mount with nothing
// watching to recover a blip; a transient timeout/dropped connection otherwise
// renders a terminal "Failed to load" card the user must reload past (#1929 —
// the workflow-parity e2e flake was the test-visible symptom). Re-fetching a
// couple of times lets an unattended load self-heal; the manual Retry card
// remains the last resort once these are exhausted.
const STATES_FETCH_ATTEMPTS = 3;
const STATES_FETCH_BACKOFF_MS = 300;

export type TGroupedProjectStates = Record<TProjectStateGroup, TProjectState[]>;

/** The outcome of one {@link ProjectStatesStore.fetchStates} attempt: the list
 *  it managed to load (possibly `[]`) plus the error of THAT call when it
 *  failed. The pickable load decides its 403 fallback off this per-call error
 *  rather than the store-wide `fetchError`, which a concurrent fetch for
 *  another workspace could overwrite between the set and the read. */
type TFetchStatesResult = { states: TProjectState[]; error: Error | null };

function emptyGroups(): TGroupedProjectStates {
  return {
    backlog: [],
    unstarted: [],
    started: [],
    completed: [],
    cancelled: [],
  };
}

export class ProjectStatesStore {
  isLoading = false;
  fetchError: Error | null = null;
  isMutating = false;
  mutatingStateId: string | null = null;
  statesByWorkspace: Map<string, TProjectState[]> = new Map();

  private rootStore: RootStore;
  /** In-flight {@link ensureStates} loads, keyed by slug. Carries the per-call
   *  result (list + that call's error) so a deduped caller — the pickable load
   *  — can decide its 403 fallback off the SAME request's error instead of the
   *  store-wide `fetchError`. Deliberately not observable: it is bookkeeping
   *  about requests, and observing it would re-render every reader of the state
   *  bank on each write. */
  private inflightStates = new Map<string, Promise<TFetchStatesResult>>();
  /** In-flight {@link ensurePickableStates} loads, keyed by slug. Same shape and
   *  same reasoning as {@link inflightStates}; separate map because the pickable
   *  load outlives the workspace-list load it wraps when it falls back to the
   *  project-scoped endpoint. */
  private inflightPickableStates = new Map<string, Promise<TProjectState[]>>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        statesByWorkspace: observable,
        inflightStates: false,
        inflightPickableStates: false,
      } as never,
      { autoBind: true },
    );
  }

  /** Whether {@link ensureStates} or {@link ensurePickableStates} has a request
   *  outstanding for this slug.
   *
   *  Deliberately not `isLoading`: that flag is store-wide, so a fetch for
   *  another workspace would show or clear this workspace's picker skeleton.
   *  Reads the in-flight maps, which are keyed by slug. Not observable — see
   *  {@link inflightStates} — so a caller re-renders on the bank arriving
   *  rather than on the request starting, which is the transition that matters.
   *  The pickable map keeps this true across the NG-2510 403 fallback too, so a
   *  picker shows its loading rows until the bank actually arrives. */
  isEnsuring(slug: string): boolean {
    return this.inflightStates.has(slug) || this.inflightPickableStates.has(slug);
  }

  /**
   * Load the workspace's state bank unless it is already cached, sharing one
   * in-flight request between concurrent callers. Mirrors
   * `WorkspaceCustomFieldsStore.ensure`.
   *
   * The bank is only needed to populate the status PICKER — every read-only
   * surface renders `state_detail` off the project payload — so callers fetch
   * it when a picker opens rather than on page load (NG-980).
   */
  async ensureStates(slug: string): Promise<TProjectState[]> {
    const cached = this.statesByWorkspace.get(slug);
    if (cached) return cached;
    const inflight = this.inflightStates.get(slug);
    if (inflight) return inflight.then((r) => r.states);
    const p = this.fetchStatesResult(slug).finally(() => {
      this.inflightStates.delete(slug);
    });
    this.inflightStates.set(slug, p);
    return p.then((r) => r.states);
  }

  /**
   * Load the state bank for a status PICKER, falling back to the project-scoped
   * `state-options/` endpoint when the workspace list is a 403 (NG-2510).
   *
   * The workspace list (`project-states/`) is gated on `state_machine.read`, a
   * Settings permission Standard members no longer hold by default (NG-1950), so
   * a Project Lead who can save a status (`project.edit`) gets a 403 and an empty
   * picker. The fallback reads the same workspace-wide list through a door gated
   * exactly like the save, so anyone who can pick can load the options.
   *
   * Contract:
   * - A cached bank is returned as-is.
   * - Otherwise the workspace-list load runs first through
   *   {@link ensureStatesResult} (settings screens and pickers share its
   *   in-flight request and its cache write), and the fallback decision reads
   *   THAT call's error off the shared promise — never the store-wide
   *   `fetchError`.
   * - A 403 on the workspace list triggers ONE `listPickable(slug, projectId)`
   *   call; its result lands under the same slug via `setStates`, because it IS
   *   the same workspace-wide list. Any other failure keeps {@link fetchStates}'
   *   behaviour: logged, `[]`.
   * - One in-flight pickable load per slug, so ten rows opening at once make one
   *   workspace-list call and one fallback call. {@link isEnsuring} stays true
   *   across the fallback so pickers show loading rows, not "no matching status".
   *
   * `fetchStates` and `ensureStates` stay 403-as-denied: only PICKERS take the
   * fallback, never the settings screens or filters.
   */
  async ensurePickableStates(slug: string, projectId: string): Promise<TProjectState[]> {
    const cached = this.statesByWorkspace.get(slug);
    if (cached) return cached;
    const inflight = this.inflightPickableStates.get(slug);
    if (inflight) return inflight;
    const p = this.loadPickableStates(slug, projectId).finally(() => {
      this.inflightPickableStates.delete(slug);
    });
    this.inflightPickableStates.set(slug, p);
    return p;
  }

  private async loadPickableStates(slug: string, projectId: string): Promise<TProjectState[]> {
    const { states, error } = await this.ensureStatesResult(slug);
    // `fetchStates` never throws: it logs and returns `[]`. A non-empty list is
    // therefore the success answer. On `[]`, the SAME call's error says why —
    // carried through the shared promise, never read off the store-wide
    // `fetchError`, so a concurrent fetch for another workspace cannot flip
    // this decision.
    if (states.length > 0) return states;
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    if (status !== 403) return states;
    try {
      const pickable = await this.rootStore.projectStateService.listPickable(slug, projectId);
      runInAction(() => {
        this.setStates(slug, pickable);
      });
      return pickable;
    } catch (err) {
      console.warn("Project state options fallback failed", { slug, projectId, err });
      return [];
    }
  }

  /** {@link ensureStates} with the call's error attached, for callers — only
   *  {@link loadPickableStates} — that must branch on WHY an empty bank came
   *  back. Shares the same per-slug in-flight request and cache write. */
  private async ensureStatesResult(slug: string): Promise<TFetchStatesResult> {
    const cached = this.statesByWorkspace.get(slug);
    if (cached) return { states: cached, error: null };
    const inflight = this.inflightStates.get(slug);
    if (inflight) return inflight;
    const p = this.fetchStatesResult(slug).finally(() => {
      this.inflightStates.delete(slug);
    });
    this.inflightStates.set(slug, p);
    return p;
  }

  getStates(workspaceSlug: string): TProjectState[] {
    return this.statesByWorkspace.get(workspaceSlug) ?? [];
  }

  getGroupedStates(workspaceSlug: string): TGroupedProjectStates {
    const groups = emptyGroups();
    const states = this.getStates(workspaceSlug);
    for (const state of states) {
      const bucket = groups[state.group];
      if (bucket) bucket.push(state);
    }
    for (const group of PROJECT_STATE_GROUPS) {
      groups[group] = [...groups[group]].sort((a, b) => a.sequence - b.sequence);
    }
    return groups;
  }

  private setStates(workspaceSlug: string, states: TProjectState[]): void {
    this.statesByWorkspace.set(workspaceSlug, states);
  }

  async fetchStates(slug: string): Promise<TProjectState[]> {
    return (await this.fetchStatesResult(slug)).states;
  }

  /** The single fetch path. Runs the retry loop, records the outcome on the
   *  store-wide flags the settings screens read (`isLoading`, `fetchError`),
   *  and returns the list plus the call's own error so a deduped caller can
   *  branch on WHY an empty bank came back without re-reading shared state.
   *  Never throws: failures log and surface as `{ states: [], error }`. */
  private async fetchStatesResult(slug: string): Promise<TFetchStatesResult> {
    this.isLoading = true;
    this.fetchError = null;
    try {
      const states = await this.listWithRecovery(slug);
      runInAction(() => {
        this.setStates(slug, states);
      });
      return { states, error: null };
    } catch (err) {
      runInAction(() => {
        this.fetchError = err as Error;
      });
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        console.warn("Project states settings bank is not permitted", { slug, err });
      } else {
        console.error("Failed to fetch project states", { slug, err });
      }
      return { states: [], error: err as Error };
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Load central states, re-fetching a transient no-real-response failure
   * (client timeout or dropped connection) a few times before surfacing it.
   * Keeps ``isLoading`` true across attempts so the Workflow tabs hold their
   * skeleton and self-heal, instead of flashing the terminal error card that
   * only a manual reload/Retry recovers (#1929). A 4xx/5xx or a cancel is not
   * recoverable and propagates on the first occurrence.
   */
  private async listWithRecovery(slug: string): Promise<TProjectState[]> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= STATES_FETCH_ATTEMPTS; attempt++) {
      try {
        return await this.rootStore.projectStateService.list(slug);
      } catch (err) {
        if (!isRecoverableFetchError(err) || attempt === STATES_FETCH_ATTEMPTS) throw err;
        lastError = err;
        console.warn(
          `[project-states] recoverable fetch failure (attempt ${attempt}/${STATES_FETCH_ATTEMPTS}); retrying`,
          { slug, err },
        );
        // Cancellable backoff — this load is started on mount with nothing
        // awaiting it, so a retry that outlives its host environment has no
        // caller left to catch what it throws (#4200).
        if (!(await waitBeforeRetry(STATES_FETCH_BACKOFF_MS * attempt))) throw err;
      }
    }
    // Unreachable: the loop returns or throws. Satisfies the type checker.
    throw lastError;
  }

  async createState(
    slug: string,
    data: TProjectStateCreateData,
    source: AnalyticsSource = "page",
  ): Promise<TProjectState> {
    this.isMutating = true;
    try {
      const created = await this.rootStore.projectStateService.create(slug, data);
      runInAction(() => {
        this.setStates(slug, [...this.getStates(slug), created]);
      });
      track("project_state_created", { state_id: created.id, group: created.group, source });
      return created;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async updateState(
    slug: string,
    stateId: string,
    data: TProjectStateUpdateData,
    source: AnalyticsSource = "page",
  ): Promise<TProjectState> {
    const snapshot = this.getStates(slug);
    const target = snapshot.find((s) => s.id === stateId);
    // Read before the optimistic patch: `fields` is what this save changed.
    const fields = changedFields(data, target);
    runInAction(() => {
      this.mutatingStateId = stateId;
      if (target) {
        this.setStates(
          slug,
          snapshot.map((s) => (s.id === stateId ? ({ ...s, ...data } as TProjectState) : s)),
        );
      }
    });
    try {
      const updated = await this.rootStore.projectStateService.update(slug, stateId, data);
      runInAction(() => {
        this.setStates(
          slug,
          this.getStates(slug).map((s) => (s.id === stateId ? updated : s)),
        );
      });
      track("project_state_updated", { state_id: stateId, fields, source });
      return updated;
    } catch (error) {
      runInAction(() => {
        this.setStates(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }

  async deleteState(
    slug: string,
    stateId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const snapshot = this.getStates(slug);
    runInAction(() => {
      this.mutatingStateId = stateId;
      this.setStates(
        slug,
        snapshot.filter((s) => s.id !== stateId),
      );
    });
    try {
      await this.rootStore.projectStateService.remove(slug, stateId);
      track("project_state_deleted", { state_id: stateId, source });
    } catch (error) {
      runInAction(() => {
        this.setStates(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }

  async fetchUsage(slug: string, stateId: string): Promise<TProjectStateUsage> {
    return this.rootStore.projectStateService.usage(slug, stateId);
  }

  async markDefault(
    slug: string,
    stateId: string,
    source: AnalyticsSource = "page",
  ): Promise<TProjectState> {
    const snapshot = this.getStates(slug);
    runInAction(() => {
      this.mutatingStateId = stateId;
      this.setStates(
        slug,
        snapshot.map((s) => {
          if (s.id === stateId) return { ...s, default: true };
          if (s.default) return { ...s, default: false };
          return s;
        }),
      );
    });
    try {
      const updated = await this.rootStore.projectStateService.markDefault(slug, stateId);
      runInAction(() => {
        this.setStates(
          slug,
          this.getStates(slug).map((s) => (s.id === stateId ? updated : s)),
        );
      });
      track("project_state_updated", { state_id: stateId, fields: ["default"], source });
      return updated;
    } catch (error) {
      runInAction(() => {
        this.setStates(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }

  async reorder(
    slug: string,
    stateId: string,
    destinationGroup: TProjectStateGroup,
    destinationIndex: number,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const snapshot = [...this.getStates(slug)];
    const target = snapshot.find((s) => s.id === stateId);
    if (!target) return;

    const grouped = this.getGroupedStates(slug);
    const sourceGroup = target.group;
    const isSameGroup = sourceGroup === destinationGroup;

    const withoutSource = grouped[sourceGroup].filter((s) => s.id !== stateId);
    const destBucket = isSameGroup ? withoutSource : [...grouped[destinationGroup]];

    const clampedIndex = Math.max(0, Math.min(destinationIndex, destBucket.length));
    const moved = { ...target, group: destinationGroup };
    destBucket.splice(clampedIndex, 0, moved);

    const resequence = (bucket: TProjectState[]): TProjectState[] =>
      bucket.map((state, idx) => ({ ...state, sequence: idx + 1 }));

    const nextGrouped: TGroupedProjectStates = { ...grouped };
    if (isSameGroup) {
      nextGrouped[destinationGroup] = resequence(destBucket);
    } else {
      nextGrouped[sourceGroup] = resequence(withoutSource);
      nextGrouped[destinationGroup] = resequence(destBucket);
    }

    const flat: TProjectState[] = [];
    for (const group of PROJECT_STATE_GROUPS) {
      flat.push(...nextGrouped[group]);
    }

    runInAction(() => {
      this.setStates(slug, flat);
      this.mutatingStateId = stateId;
    });

    try {
      const updatedFromServer = await this.rootStore.projectStateService.update(slug, stateId, {
        group: destinationGroup,
        sequence: clampedIndex + 1,
      });
      runInAction(() => {
        this.setStates(
          slug,
          this.getStates(slug).map((s) => (s.id === stateId ? updatedFromServer : s)),
        );
      });
      track("project_states_reordered", {
        state_id: stateId,
        group: destinationGroup,
        regrouped: !isSameGroup,
        source,
      });
    } catch (error) {
      runInAction(() => {
        this.setStates(slug, snapshot);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingStateId = null;
      });
    }
  }
}
